import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Icon } from "./Icon.js";
import "./Toast.css";

export type ToastTone = "default" | "ok" | "danger";

export interface ToastOptions {
  tone?: ToastTone;
  /**
   * Milliseconds before auto-dismiss. Defaults to ~2.5s, except for `danger`
   * toasts, which stay until dismissed — an error that vanishes before it can
   * be screenshotted or reported is not much use.
   */
  duration?: number;
  /** Plain text behind a "Copy details" button (danger toasts). Overrides `describeError`. */
  details?: string;
}

interface ToastEntry {
  id: number;
  message: string;
  tone: ToastTone;
  details?: string;
  /** How many times this identical toast has been raised. */
  count: number;
}

export interface ToastContextValue {
  toast: (message: string, opts?: ToastOptions) => void;
  /** Drops every toast — for when what they describe no longer applies, like a signed-out session's errors. */
  clear: () => void;
}

export interface ToastProviderProps {
  children: ReactNode;
  /**
   * Called when a danger toast is raised without explicit `details`, so the
   * app can attach what it knows (last failed request, version, office...).
   */
  describeError?: (message: string) => string | undefined;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 2500;
/** Persistent errors would otherwise pile up the screen; the oldest give way. */
const MAX_VISIBLE = 3;

export function ToastProvider({ children, describeError }: ToastProviderProps) {
  const [entries, setEntries] = useState<ToastEntry[]>([]);
  const idRef = useRef(0);
  // Mirrors `entries` so a toast raised twice in one tick still sees the first.
  const entriesRef = useRef<ToastEntry[]>([]);
  const timers = useRef(new Map<number, number>());
  const describeRef = useRef(describeError);
  useEffect(() => {
    describeRef.current = describeError;
  }, [describeError]);

  const dismiss = useCallback((id: number) => {
    window.clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    entriesRef.current = entriesRef.current.filter((entry) => entry.id !== id);
    setEntries(entriesRef.current);
  }, []);

  const schedule = useCallback(
    (id: number, ms: number | undefined) => {
      window.clearTimeout(timers.current.get(id));
      timers.current.delete(id);
      if (ms === undefined) return;
      timers.current.set(id, window.setTimeout(() => dismiss(id), ms));
    },
    [dismiss]
  );

  const toast = useCallback(
    (message: string, opts?: ToastOptions) => {
      const tone = opts?.tone ?? "default";
      const details = tone === "danger" ? (opts?.details ?? describeRef.current?.(message)) : undefined;
      const ms = opts?.duration ?? (tone === "danger" ? undefined : DEFAULT_DURATION);

      const prev = entriesRef.current;
      const match = prev.find((entry) => entry.message === message && entry.tone === tone);
      let id: number;
      let next: ToastEntry[];
      if (match) {
        id = match.id;
        next = prev.map((entry) =>
          entry.id === id ? { ...entry, count: entry.count + 1, details: details ?? entry.details } : entry
        );
      } else {
        id = ++idRef.current;
        next = [...prev, { id, message, tone, details, count: 1 }];
        // Cap the stack; whatever falls off loses its pending timer too.
        for (const dropped of next.slice(0, -MAX_VISIBLE)) {
          window.clearTimeout(timers.current.get(dropped.id));
          timers.current.delete(dropped.id);
        }
        next = next.slice(-MAX_VISIBLE);
      }
      entriesRef.current = next;
      setEntries(next);
      schedule(id, ms);
    },
    [schedule]
  );

  const clear = useCallback(() => {
    for (const timer of timers.current.values()) window.clearTimeout(timer);
    timers.current.clear();
    entriesRef.current = [];
    setEntries([]);
  }, []);

  const value = useMemo(() => ({ toast, clear }), [toast, clear]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* role="status" + aria-live="polite" so screen readers announce each
          toast without stealing focus — it's a passive notification, not a
          dialog the user needs to act on. Errors are the exception: they use
          role="alert" so they are announced promptly. */}
      <div className="ui-toast-container" role="status" aria-live="polite">
        {entries.map((entry) => (
          <ToastItem key={entry.id} entry={entry} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ entry, onDismiss }: { entry: ToastEntry; onDismiss: (id: number) => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!entry.details) return;
    try {
      await navigator.clipboard.writeText(entry.details);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked (insecure origin, permissions); the details
      // stay on screen via the title attribute rather than failing loudly.
    }
  };

  return (
    <div className={`ui-toast ui-toast--${entry.tone}`} role={entry.tone === "danger" ? "alert" : undefined}>
      {entry.tone === "ok" && (
        <span className="ui-toast__icon">
          <Icon name="check" size={12} strokeWidth={3} />
        </span>
      )}
      {entry.tone === "danger" && (
        <span className="ui-toast__icon">
          <Icon name="warning" size={12} strokeWidth={3} />
        </span>
      )}
      <span>{entry.message}</span>
      {entry.count > 1 && <span className="ui-toast__count">×{entry.count}</span>}
      {entry.tone === "danger" && (
        <>
          {entry.details && (
            <button type="button" className="ui-toast__action" onClick={copy}>
              {copied ? "Copied" : "Copy details"}
            </button>
          )}
          <button type="button" className="ui-toast__action" aria-label="Dismiss" onClick={() => onDismiss(entry.id)}>
            ✕
          </button>
        </>
      )}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
}
